import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	InsuranceReportState,
	InsuranceReportStateError,
	useInsuranceReportsUpdateMutation,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import SelectField from '../../../../Components/Form/components/SelectField';
import {
	STATE_ERROR_OPTIONS,
	STATE_OPTIONS,
} from '../InsuranceReportUpdate/InsuranceReportUpdate';

interface InsuranceReportBulkUpdateValues {
	state?: {
		label: string;
		value: InsuranceReportState;
	};
	stateError?: {
		label: string;
		value: InsuranceReportStateError;
	};
}

interface Props {
	ids: string[];
	onUpdate: () => void;
}

const InsuranceReportBulkUpdate: React.FC<Props> = ({ ids, onUpdate }) => {
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [updateInsuranceReports] = useInsuranceReportsUpdateMutation();

	const formMethods = useForm<InsuranceReportBulkUpdateValues>();

	const handleSubmit = async (values: InsuranceReportBulkUpdateValues) => {
		if (!values.state && !values.stateError) {
			showNotificationAlert({
				content: t('validation.insuranceReportBulkUpdate.required'),
			});
			return;
		}
		setIsLoading(true);
		const result = await updateInsuranceReports({
			input: ids.map(id => ({
				id,
				state: values.state?.value,
				stateError: values.stateError?.value,
			})),
		});
		setIsLoading(false);
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.insuranceReportBulkUpdate.successMessage'),
			});
			onUpdate();
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<InputWrapper>
				<SelectField
					name='state'
					options={STATE_OPTIONS}
					label='component.insuranceReportCreate.state'
					portal
				/>
			</InputWrapper>
			<InputWrapper>
				<SelectField
					name='stateError'
					options={STATE_ERROR_OPTIONS}
					label='component.insuranceReportCreate.stateError'
					portal
				/>
			</InputWrapper>
			<SubmitButton onSubmit={handleSubmit} title='base.shared.edit' />
		</FormWrapper>
	);
};

export default InsuranceReportBulkUpdate;
